import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ImagePlus, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr, imgUrl, htmlText } from "@/lib/api";
import { RichEditor } from "@/components/desk/RichEditor";
import { useSite } from "@/context/SiteContext";
import { PageHead } from "@/components/desk/DeskLayout";
import { StatusChip } from "@/components/desk/StatusChip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field } from "@/components/AuthShell";

const EMPTY = { en: { headline: "", summary: "", content: "" }, ta: { headline: "", summary: "", content: "" },
  category_id: "", tags: [], trend_type: "regular", trend_duration_hours: 24, cover_image: "", images: [], reported_by: "", event_datetime: "" };
const toLocal = (iso) => (iso ? new Date(new Date(iso).getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16) : "");

const Uploader = ({ onDone, testId, children }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/desk/upload", fd);
      onDone(data.url);
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <>
      <input ref={ref} type="file" accept="image/*" hidden onChange={pick} data-testid={`${testId}-file`} />
      <button type="button" data-testid={testId} onClick={() => ref.current.click()} disabled={busy}
        className="w-full border border-dashed border-zinc-400 hover:border-zinc-900 bg-zinc-50 py-4 text-sm flex items-center justify-center gap-2 transition-colors">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />} {children}
      </button>
    </>
  );
};

const LangPanel = ({ lang, value, onChange }) => {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  const ta = lang === "ta";
  return (
    <div className={`space-y-4 ${ta ? "lang-ta" : ""}`} data-testid={`editor-panel-${lang}`}>
      <Field label={`Headline (${ta ? "Tamil" : "English"})`}>
        <Input data-testid={`editor-${lang}-headline`} value={value.headline} onChange={set("headline")} className="h-11 rounded-sm bg-white text-lg font-semibold" />
      </Field>
      <Field label="Summary / standfirst">
        <Textarea data-testid={`editor-${lang}-summary`} value={value.summary} onChange={set("summary")} className="rounded-sm bg-white min-h-[70px]" />
      </Field>
      <Field label="Content" hint="Use the toolbar for bold, headings, bullet/numbered lists, quotes, links and images. You can paste formatted text from Word or Google Docs.">
        <RichEditor testId={`editor-${lang}-content`} value={value.content} onChange={(html) => onChange({ ...value, content: html })}
          placeholder={ta ? "செய்தியை இங்கே எழுதுங்கள்…" : "Write the story here…"} className="text-[17px]" />
      </Field>
    </div>
  );
};

export default function StoryEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const { settings } = useSite();
  const [form, setForm] = useState({ ...EMPTY, trend_duration_hours: settings.default_trend_hours || 24 });
  const [status, setStatus] = useState("draft");
  const [meta, setMeta] = useState({ categories: [], tags: [] });
  const [tab, setTab] = useState("ta");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/desk/meta").then((r) => setMeta(r.data));
    if (id)
      api.get(`/desk/stories/${id}`).then(({ data }) => {
        if (!data.can_edit) {
          toast.error("You cannot edit this story");
          nav(`/desk/stories/${id}`);
          return;
        }
        setStatus(data.status);
        setForm({ ...EMPTY, ...data, event_datetime: toLocal(data.event_datetime), trend_type: data.trend_original || data.trend_type });
      });
  }, [id, nav]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleTag = (t) => set("tags", form.tags.includes(t) ? form.tags.filter((x) => x !== t) : [...form.tags, t]);
  const resets = ["approved", "scheduled", "published"].includes(status);

  const save = async (submit) => {
    setBusy(true);
    const body = { ...form, trend_duration_hours: Number(form.trend_duration_hours) || 0,
      event_datetime: form.event_datetime ? new Date(form.event_datetime).toISOString() : null };
    try {
      let sid = id;
      if (id) await api.put(`/desk/stories/${id}`, body);
      else sid = (await api.post("/desk/stories", body)).data.id;
      if (submit) await api.post(`/desk/stories/${sid}/action`, { action: "submit" });
      toast.success(submit ? "Submitted for approval" : "Saved");
      nav(`/desk/stories/${sid}`);
    } catch (err) {
      toast.error(fmtErr(err));
      if (!id && err.config?.url?.includes("/action")) nav("/desk/stories");
    } finally {
      setBusy(false);
    }
  };

  const done = (l) => form[l].headline.trim() && htmlText(form[l].content);
  const started = (l) => form[l].headline.trim() || htmlText(form[l].content);
  const ready = (done("en") || done("ta")) && ["en", "ta"].every((l) => !started(l) || done(l));

  return (
    <div data-testid="story-editor">
      <PageHead title={id ? "Edit story" : "New story"} sub="Write in Tamil, English or both. Each language needs its own headline and content.">
        {id && <StatusChip status={status} testId="editor-status-chip" />}
      </PageHead>
      {resets && (
        <div data-testid="editor-reapproval-warning" className="mb-4 flex gap-2 border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-900">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> This story is {status}. Saving any change sends it back to the approval queue{status === "scheduled" ? " and disables its schedule" : ""}{status === "published" ? " and removes it from the site until re-approved" : ""}.
        </div>
      )}
      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 bg-white border border-zinc-200 p-5">
          <div className="flex border-b border-zinc-200 mb-5">
            {["en", "ta"].map((l) => (
              <button key={l} type="button" data-testid={`editor-tab-${l}`} onClick={() => setTab(l)}
                className={`px-4 py-2 text-sm font-semibold -mb-px border-b-2 transition-colors flex items-center gap-2 ${tab === l ? "border-zinc-900" : "border-transparent text-zinc-500"}`}>
                {l === "en" ? "English" : "தமிழ் Tamil"}
                <span className={`h-2 w-2 rounded-full ${done(l) ? "bg-emerald-500" : started(l) ? "bg-amber-400" : "bg-zinc-300"}`} />
              </button>
            ))}
          </div>
          <LangPanel key={tab} lang={tab} value={form[tab]} onChange={(v) => set(tab, v)} />
        </div>
        <div className="lg:col-span-4 space-y-4">
          <div className="bg-white border border-zinc-200 p-5 space-y-4">
            <Field label="Category *">
              <select data-testid="editor-category" value={form.category_id} onChange={(e) => set("category_id", e.target.value)} className="h-10 w-full border border-zinc-300 bg-white px-2 text-sm rounded-sm">
                <option value="">Choose…</option>
                {meta.categories.map((c) => <option key={c.id} value={c.id}>{c.name_en} / {c.name_ta}</option>)}
              </select>
            </Field>
            <Field label="Trend type">
              <div className="grid grid-cols-3 gap-1" data-testid="editor-trend">
                {["breaking", "hot", "regular"].map((t) => (
                  <button key={t} type="button" data-testid={`editor-trend-${t}`} onClick={() => set("trend_type", t)}
                    className={`h-9 text-xs font-bold uppercase tracking-[0.08em] border rounded-sm transition-colors ${form.trend_type === t ? (t === "breaking" ? "bg-[#DC2626] text-white border-[#DC2626]" : t === "hot" ? "bg-[#EA580C] text-white border-[#EA580C]" : "bg-[#2563EB] text-white border-[#2563EB]") : "bg-white border-zinc-300"}`}>
                    {t}
                  </button>
                ))}
              </div>
            </Field>
            {form.trend_type !== "regular" && (
              <Field label="Downgrade to Regular after (hours)" hint="Counted from publish time. 0 = never downgrade.">
                <Input data-testid="editor-trend-hours" type="number" min={0} max={720} value={form.trend_duration_hours} onChange={(e) => set("trend_duration_hours", e.target.value)} className="h-10 rounded-sm" />
              </Field>
            )}
            <Field label="Reported by *">
              <Input data-testid="editor-reported-by" value={form.reported_by} onChange={(e) => set("reported_by", e.target.value)} className="h-10 rounded-sm" />
            </Field>
            <Field label="Event date / time">
              <Input data-testid="editor-event-datetime" type="datetime-local" value={form.event_datetime || ""} onChange={(e) => set("event_datetime", e.target.value)} className="h-10 rounded-sm" />
            </Field>
            <Field label="Tags">
              <div className="flex flex-wrap gap-1.5" data-testid="editor-tags">
                {meta.tags.map((t) => (
                  <button key={t.id} type="button" data-testid={`editor-tag-${t.slug}`} onClick={() => toggleTag(t.name)}
                    className={`px-2 py-1 text-xs font-mono-meta border rounded-sm ${form.tags.includes(t.name) ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-300"}`}>
                    {t.name}
                  </button>
                ))}
              </div>
            </Field>
          </div>
          <div className="bg-white border border-zinc-200 p-5 space-y-3">
            <p className="text-xs font-bold uppercase tracking-[0.1em] text-zinc-600">Cover image</p>
            {form.cover_image ? (
              <div className="relative">
                <img data-testid="editor-cover-preview" src={imgUrl(form.cover_image)} alt="" className="w-full aspect-[16/9] object-cover" />
                <button type="button" data-testid="editor-cover-remove" onClick={() => set("cover_image", "")} className="absolute top-2 right-2 bg-white p-1"><X className="h-4 w-4" /></button>
              </div>
            ) : (
              <Uploader testId="editor-cover-upload" onDone={(u) => set("cover_image", u)}>Upload cover</Uploader>
            )}
            {form.images.length > 0 && (
              <>
                <p className="text-xs font-bold uppercase tracking-[0.1em] text-zinc-600 pt-2">Older attached images ({form.images.length})</p>
                <div className="grid grid-cols-3 gap-2">
                  {form.images.map((u, i) => (
                    <div key={u} className="relative">
                      <img src={imgUrl(u)} alt="" className="aspect-square object-cover w-full" data-testid={`editor-inline-${i}`} />
                      <button type="button" onClick={() => set("images", form.images.filter((x) => x !== u))} className="absolute top-1 right-1 bg-white p-0.5" data-testid={`editor-inline-remove-${i}`}><X className="h-3 w-3" /></button>
                    </div>
                  ))}
                </div>
              </>
            )}
            <p className="text-xs text-zinc-500">To place images inside the story, use the image button in the editor toolbar.</p>
          </div>
          <div className="flex flex-col gap-2">
            <Button data-testid="editor-save-btn" variant="outline" disabled={busy} onClick={() => save(false)} className="rounded-sm h-11">Save {status === "draft" ? "draft" : "changes"}</Button>
            {["draft", "rejected", "on_hold", "withdrawn"].includes(status) && (
              <Button data-testid="editor-submit-btn" disabled={busy || !ready} onClick={() => save(true)} className="rounded-sm h-11">Save & submit for approval</Button>
            )}
            {!ready && <p className="text-xs text-zinc-500" data-testid="editor-incomplete-hint">Add a headline and content in at least one language. If you start the other language, finish both its headline and content too.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
